// Accounting — Reports
//
// Five financial views over the outlet's data (all net of GST):
//   Expense Summary · Profit & Loss · Cash Movement · Sales vs Expenses ·
//   Account Statement, plus the AR/AP ageing matrices.

import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { api } from '../lib/api.js';
import type {
  AccountStatementRow,
  AgingResult,
  CashMovementResult,
  ExpenseSummaryResult,
  ProfitLossResult,
  TrendResult,
} from '../lib/api.js';
import { OutletViewCache } from '../lib/outlet-view-cache.js';
import { TAB_UI_STYLES } from '../styles/tab-ui.js';

const money = (n: number | undefined) => (Number(n) || 0).toFixed(2);

function todayIso(): string {
  const d = new Date();
  const z = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return z.toISOString().slice(0, 10);
}

function isoToMs(v: string, endOfDay = false): number {
  if (!v) return 0;
  const d = new Date(`${v}T00:00:00`);
  if (endOfDay) d.setHours(23, 59, 59, 999);
  return d.getTime();
}

function msToIso(ms: number): string {
  if (!ms) return todayIso();
  const d = new Date(ms);
  const z = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return z.toISOString().slice(0, 10);
}

type ReportKind = 'expense-summary' | 'profit-loss' | 'cash-movement' | 'trend' | 'account-statement' | 'ar-aging' | 'ap-aging';

const REPORTS: Array<{ id: ReportKind; label: string }> = [
  { id: 'expense-summary', label: 'Expense Summary' },
  { id: 'profit-loss', label: 'Profit & Loss' },
  { id: 'cash-movement', label: 'Cash Movement' },
  { id: 'trend', label: 'Sales vs Expenses' },
  { id: 'account-statement', label: 'Account Statement' },
  { id: 'ar-aging', label: 'Customer Ageing' },
  { id: 'ap-aging', label: 'Supplier Ageing' },
];

interface KeptReportView {
  report: ReportKind;
  from: string;
  to: string;
  asOf: string;
}

@customElement('accounting-reports')
export class AccountingReports extends LitElement {
  static styles = [TAB_UI_STYLES, css`
    :host { display: flex; flex-direction: column; height: 100%; min-height: 0; overflow: hidden; }
    .bar { display: flex; flex-wrap: wrap; gap: 0.5rem; align-items: end; margin: 0.75rem 1rem 0.5rem; flex-shrink: 0; }
    .bar .fg { margin: 0; }
    .bar input, .bar select { min-height: 38px; padding: 7px 10px; border: 1px solid var(--color-border); border-radius: 10px; background: var(--color-surface); color: var(--color-text); font-family: inherit; font-size: 0.84rem; }
    .chips { display: flex; flex-wrap: wrap; gap: 0.35rem; margin: 0 1rem 0.5rem; flex-shrink: 0; }
    .chip-report { border: 1px solid var(--color-border); background: var(--color-surface); border-radius: 999px; padding: 6px 12px; font-family: inherit; font-size: 0.78rem; font-weight: 600; cursor: pointer; color: var(--color-text-secondary); }
    .chip-report.active { background: var(--color-primary); border-color: var(--color-primary); color: #fff; }
    .body { flex: 1; min-height: 0; overflow: auto; padding: 0 1rem 1rem; }
    .section { margin-bottom: 1rem; }
    .section h4 { margin: 0.2rem 0 0.5rem; font-size: 0.85rem; }
    .wrap { border: 1px solid var(--color-border); border-radius: 12px; overflow: auto; background: var(--color-surface); }
    tfoot td { font-weight: 700; background: #f8fafd; }
    .pl { max-width: 640px; }
    .pl tr.total td { font-weight: 700; border-top: 2px solid var(--color-border); }
    .pl tr.net td { font-weight: 800; font-size: 0.95rem; background: #eef4ff; }
    .muted { color: var(--color-text-secondary); font-size: 0.78rem; }
    .state { padding: 2rem; text-align: center; color: var(--color-text-secondary); }
    .error { margin: 0 1rem 0.5rem; padding: 0.6rem 0.8rem; border-radius: 10px; background: #fdecea; color: #b71c1c; font-size: 0.84rem; }
  `];

  @property({ type: String }) storeCode = '';

  @state() private _report: ReportKind = 'expense-summary';
  @state() private _from = msToIso(new Date(new Date().getFullYear(), new Date().getMonth(), 1).getTime());
  @state() private _to = todayIso();
  @state() private _asOf = todayIso();
  @state() private _loading = false;
  @state() private _error = '';
  @state() private _data: any = null;

  private _view = new OutletViewCache<KeptReportView>(this, 'accounting-reports', {
    capture: () => ({ report: this._report, from: this._from, to: this._to, asOf: this._asOf }),
    restore: (v) => {
      this._report = v.report;
      this._from = v.from;
      this._to = v.to;
      this._asOf = v.asOf;
    },
    reset: () => {
      this._report = 'expense-summary';
      this._from = msToIso(new Date(new Date().getFullYear(), new Date().getMonth(), 1).getTime());
      this._to = todayIso();
      this._asOf = todayIso();
      this._data = null;
    },
  });

  connectedCallback() {
    super.connectedCallback();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._view.dispose();
  }

  protected updated(changed: Map<PropertyKey, unknown>) {
    // switchTo() returns 'unchanged' | 'restored' | 'fresh' — compare it, never
    // treat it as a boolean: every one of those strings is truthy, and a
    // truthy check here re-runs the loader on every render (endless loop).
    const switched = this._view.switchTo(this.storeCode);
    if (switched === 'unchanged' && !changed.has('storeCode')) return;
    // The report payload is not part of the kept snapshot — refetch for the
    // outlet being entered instead of showing the previous outlet's numbers.
    this._data = null;
    void this._load();
  }

  private async _load() {
    if (!this.storeCode) return;
    const epoch = this._view.epoch;
    this._loading = true;
    this._error = '';
    try {
      const res = await api.accountingReport(this.storeCode, this._report, {
        from: isoToMs(this._from, false),
        to: isoToMs(this._to, true),
        asOf: isoToMs(this._asOf, true),
      });
      if (!this._view.isCurrent(epoch)) return;
      this._data = res.data;
    } catch (e: any) {
      if (this._view.isCurrent(epoch)) {
        this._error = e?.message || 'Failed to run the report';
        this._data = null;
      }
    } finally {
      if (this._view.isCurrent(epoch)) this._loading = false;
    }
  }

  private _pick(r: ReportKind) {
    this._report = r;
    this._data = null;
    void this._load();
  }

  private _exportCsv() {
    const d = this._data;
    if (!d) return;
    const rows: string[][] = [];
    const push = (a: unknown[]) => rows.push(a.map((v) => (v == null ? '' : String(v))));
    if (this._report === 'expense-summary') {
      const x = d as ExpenseSummaryResult;
      push(['Category', 'Entries', 'Gross', 'Tax', 'Net']);
      for (const r of x.categories) push([r.category, r.entries, money(r.gross), money(r.tax), money(r.net)]);
      rows.push([]);
      push(['Month', 'Gross', 'Net']);
      for (const m of x.months) push([m.month, money(m.gross), money(m.net)]);
    } else if (this._report === 'profit-loss') {
      const x = d as ProfitLossResult;
      push(['Line', 'Amount']);
      push(['Revenue (net)', money(x.revenue)]);
      push(['Cost of goods sold', money(x.cogs)]);
      push(['Gross profit', money(x.grossProfit)]);
      for (const c of x.expensesByCategory) push([`Expense: ${c.category}`, money(c.net)]);
      push(['Payment fees', money(x.fees)]);
      push(['Net profit', money(x.netProfit)]);
    } else if (this._report === 'cash-movement') {
      const x = d as CashMovementResult;
      push(['Type', 'Method', 'Amount']);
      for (const r of x.receipts) push(['Receipt', r.method, money(r.amount)]);
      for (const r of x.payments) push(['Supplier payment', r.method, money(r.amount)]);
      for (const r of x.expenses) push(['Expense', r.method, money(r.amount)]);
      push(['Net', '', money(x.net)]);
    } else if (this._report === 'trend') {
      const x = d as TrendResult;
      push(['Month', 'Sales', 'COGS', 'Gross profit', 'Expenses', 'Net profit']);
      for (const r of x.rows) push([r.month, money(r.sales), money(r.cogs), money(r.grossProfit), money(r.expenses), money(r.netProfit)]);
    } else if (this._report === 'account-statement') {
      push(['Account', 'Opening', 'In', 'Out', 'Closing']);
      for (const r of d.accounts as AccountStatementRow[])
        push([r.name, money(r.opening), money(r.inflow), money(r.outflow), money(r.closing)]);
    } else {
      const x = d as AgingResult;
      push(['Party', 'Current', '1-30', '31-60', '61-90', '90+', 'Total']);
      for (const p of x.parties)
        push([p.partyName, money(p.current), money(p.d1_30), money(p.d31_60), money(p.d61_90), money(p.d90p), money(p.total)]);
    }
    const csv = rows.map((r) => r.map((c) => `"${c.replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${this._report}-${this._from}-${this._to}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  private _renderBody() {
    const d = this._data;
    if (!d) return html`<div class="state">${this._loading ? 'Running report…' : 'No data.'}</div>`;

    if (this._report === 'expense-summary') {
      const x = d as ExpenseSummaryResult;
      return html`
        <div class="section">
          <h4>By category</h4>
          <div class="wrap"><table>
            <thead><tr><th>Category</th><th class="num">Entries</th><th class="num">Gross</th><th class="num">Tax</th><th class="num">Net of GST</th></tr></thead>
            <tbody>
              ${x.categories.map((r) => html`<tr><td>${r.category}</td><td class="num">${r.entries}</td>
                <td class="num">${money(r.gross)}</td><td class="num">${r.tax ? money(r.tax) : '—'}</td>
                <td class="num">${money(r.net)}</td></tr>`)}
              ${!x.categories.length ? html`<tr><td colspan="5" class="state">No expenses in this period.</td></tr>` : ''}
            </tbody>
            ${x.categories.length ? html`<tfoot><tr><td>Total</td><td class="num"></td>
              <td class="num">${money(x.categories.reduce((s, r) => s + r.gross, 0))}</td>
              <td class="num">${money(x.categories.reduce((s, r) => s + r.tax, 0))}</td>
              <td class="num">${money(x.categories.reduce((s, r) => s + r.net, 0))}</td></tr></tfoot>` : ''}
          </table></div>
        </div>
        <div class="section">
          <h4>By month</h4>
          <div class="wrap"><table>
            <thead><tr><th>Month</th><th class="num">Gross</th><th class="num">Net of GST</th></tr></thead>
            <tbody>${x.months.map((m) => html`<tr><td>${m.month}</td><td class="num">${money(m.gross)}</td><td class="num">${money(m.net)}</td></tr>`)}</tbody>
          </table></div>
        </div>`;
    }

    if (this._report === 'profit-loss') {
      const x = d as ProfitLossResult;
      return html`
        <div class="section pl">
          <h4>Profit &amp; Loss (net of GST)</h4>
          <div class="wrap"><table>
            <tbody>
              <tr><td>Revenue</td><td class="num">${money(x.revenue)}</td></tr>
              <tr><td>Less: Cost of goods sold</td><td class="num">(${money(x.cogs)})</td></tr>
              <tr class="total"><td>Gross profit</td><td class="num">${money(x.grossProfit)}</td></tr>
              ${x.expensesByCategory.map((c) => html`<tr><td>&nbsp;&nbsp;${c.category}</td><td class="num">(${money(c.net)})</td></tr>`)}
              <tr><td>Less: Payment fees</td><td class="num">(${money(x.fees)})</td></tr>
              <tr class="net"><td>Net profit</td><td class="num">${money(x.netProfit)}</td></tr>
            </tbody>
          </table></div>
          <div class="muted" style="margin-top:0.4rem">Output tax collected: RM ${money(x.outputTax)} (informational).</div>
        </div>`;
    }

    if (this._report === 'cash-movement') {
      const x = d as CashMovementResult;
      const tbl = (title: string, rows: Array<{ method: string; amount: number }>) => html`
        <div class="section">
          <h4>${title}</h4>
          <div class="wrap"><table>
            <tbody>${rows.map((r) => html`<tr><td>${r.method}</td><td class="num">${money(r.amount)}</td></tr>`)}
              ${!rows.length ? html`<tr><td class="state">None.</td></tr>` : ''}</tbody>
          </table></div>
        </div>`;
      return html`
        ${tbl('Money in (sales receipts)', x.receipts)}
        ${tbl('Supplier payments', x.payments)}
        ${tbl('Expenses', x.expenses)}
        <div class="section pl"><div class="wrap"><table>
          <tr class="total"><td>Total in</td><td class="num">${money(x.inTotal)}</td></tr>
          <tr class="total"><td>Total out</td><td class="num">${money(x.outTotal)}</td></tr>
          <tr class="net"><td>Net movement</td><td class="num">${money(x.net)}</td></tr>
        </table></div></div>`;
    }

    if (this._report === 'trend') {
      const x = d as TrendResult;
      return html`<div class="section"><h4>Monthly sales vs expenses</h4><div class="wrap"><table>
        <thead><tr><th>Month</th><th class="num">Sales</th><th class="num">COGS</th><th class="num">Gross profit</th><th class="num">Expenses</th><th class="num">Net profit</th></tr></thead>
        <tbody>${x.rows.map((r) => html`<tr><td>${r.month}</td><td class="num">${money(r.sales)}</td>
          <td class="num">${money(r.cogs)}</td><td class="num">${money(r.grossProfit)}</td>
          <td class="num">${money(r.expenses)}</td><td class="num"><strong>${money(r.netProfit)}</strong></td></tr>`)}
          ${!x.rows.length ? html`<tr><td colspan="6" class="state">No activity in this period.</td></tr>` : ''}
        </tbody>
      </table></div></div>`;
    }

    if (this._report === 'account-statement') {
      const rows = (d.accounts ?? []) as AccountStatementRow[];
      return html`<div class="section"><h4>Account movement</h4><div class="wrap"><table>
        <thead><tr><th>Account</th><th>Type</th><th class="num">Opening</th><th class="num">In</th><th class="num">Out</th><th class="num">Closing</th></tr></thead>
        <tbody>${rows.map((r) => html`<tr><td>${r.name}</td><td>${r.kind}</td>
          <td class="num">${money(r.opening)}</td><td class="num">${money(r.inflow)}</td>
          <td class="num">${money(r.outflow)}</td><td class="num"><strong>${money(r.closing)}</strong></td></tr>`)}
          ${!rows.length ? html`<tr><td colspan="6" class="state">No mapped accounts.</td></tr>` : ''}
        </tbody>
      </table></div>
      <div class="muted" style="margin-top:0.4rem">Opening = all movements before the period start (including opening balances). Online clearing uses the per-payment net reported by the provider.</div>
      </div>`;
    }

    // Ageing
    const x = d as AgingResult;
    return html`
      <div class="section">
        <h4>${this._report === 'ar-aging' ? 'Customer ageing (receivables)' : 'Supplier ageing (payables)'} — as at ${msToIso(x.asOf)}</h4>
        <div class="wrap"><table>
          <thead><tr><th>Party</th><th class="num">Current</th><th class="num">1–30</th><th class="num">31–60</th><th class="num">61–90</th><th class="num">90+</th><th class="num">Total</th></tr></thead>
          <tbody>${x.parties.map((p) => html`<tr><td>${p.partyName || `#${p.partyId}`}</td>
            <td class="num">${money(p.current)}</td><td class="num">${money(p.d1_30)}</td>
            <td class="num">${money(p.d31_60)}</td><td class="num">${money(p.d61_90)}</td>
            <td class="num">${money(p.d90p)}</td><td class="num"><strong>${money(p.total)}</strong></td></tr>`)}
            ${!x.parties.length ? html`<tr><td colspan="7" class="state">Nothing outstanding.</td></tr>` : ''}
          </tbody>
          ${x.parties.length ? html`<tfoot><tr><td>Total</td>
            <td class="num">${money(x.parties.reduce((s, p) => s + p.current, 0))}</td>
            <td class="num">${money(x.parties.reduce((s, p) => s + p.d1_30, 0))}</td>
            <td class="num">${money(x.parties.reduce((s, p) => s + p.d31_60, 0))}</td>
            <td class="num">${money(x.parties.reduce((s, p) => s + p.d61_90, 0))}</td>
            <td class="num">${money(x.parties.reduce((s, p) => s + p.d90p, 0))}</td>
            <td class="num">${money(x.parties.reduce((s, p) => s + p.total, 0))}</td></tr></tfoot>` : ''}
        </table></div>
      </div>
      <div class="section">
        <h4>Outstanding invoices</h4>
        <div class="wrap"><table>
          <thead><tr><th>Party</th><th>Ref</th><th>Date</th><th>Due</th><th class="num">Invoice (incl. GST)</th><th class="num">Paid</th><th class="num">Outstanding</th><th class="num">Days</th></tr></thead>
          <tbody>${x.invoices.map((i) => html`<tr><td>${i.partyName || `#${i.partyId}`}</td><td>${i.refNo || i.id}</td>
            <td>${msToIso(i.date)}</td><td>${msToIso(i.dueDate)}</td>
            <td class="num">${money(i.invoiceAmount)}</td><td class="num">${money(i.paid)}</td>
            <td class="num">${money(i.outstanding)}</td>
            <td class="num">${Math.max(0, Math.round(i.daysOverdue))}</td></tr>`)}
            ${!x.invoices.length ? html`<tr><td colspan="8" class="state">Nothing outstanding.</td></tr>` : ''}
          </tbody>
        </table></div>
      </div>`;
  }

  render() {
    if (!this.storeCode) return html`<div class="state">No outlet selected.</div>`;
    return html`
      <div class="chips">
        ${REPORTS.map((r) => html`<button class="chip-report ${this._report === r.id ? 'active' : ''}"
          @click=${() => this._pick(r.id)}>${r.label}</button>`)}
      </div>
      <div class="bar">
        <div class="fg"><label>From</label>
          <input type="date" .value=${this._from} @change=${(ev: Event) => { this._from = (ev.target as HTMLInputElement).value; void this._load(); }}></div>
        <div class="fg"><label>To</label>
          <input type="date" .value=${this._to} @change=${(ev: Event) => { this._to = (ev.target as HTMLInputElement).value; void this._load(); }}></div>
        ${this._report === 'ar-aging' || this._report === 'ap-aging' ? html`
          <div class="fg"><label>As at</label>
            <input type="date" .value=${this._asOf} @change=${(ev: Event) => { this._asOf = (ev.target as HTMLInputElement).value; void this._load(); }}></div>
        ` : ''}
        <button class="btn" @click=${() => void this._load()} ?disabled=${this._loading}>↻ Run</button>
        <button class="btn-sec btn" @click=${() => this._exportCsv()} ?disabled=${!this._data}>⬇ CSV</button>
      </div>
      ${this._error ? html`<div class="error">${this._error}</div>` : ''}
      <div class="body">${this._loading && !this._data ? html`<div class="state">Running report…</div>` : this._renderBody()}</div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'accounting-reports': AccountingReports;
  }
}
